<template>
  <div class="user-add-credit-box">
    <AmountSelect add-standard-options :value="amount" @input="amount = $event" />

    <b-row v-if="amount > 0">
      <b-col sm="6">
        <strong>{{ $t("components.user.add_credit_box.total.label") }}</strong>
        <p class="total">{{ amountWithFee | currency }}</p>
        <b-form-text>
          {{ $t("components.user.add_credit_box.total.fees.label") }}&nbsp;:
          <pre>{{ feeRatio | percent }} + {{ feeConstant | currency }}</pre>
        </b-form-text>
      </b-col>
      <b-col sm="6">
        <strong>{{ $t("components.user.add_credit_box.payment_method.label") }}</strong>
        <b-form-select
          v-if="hasPaymentMethod"
          id="payment_method_id"
          v-model="paymentMethodId"
          name="payment_method_id"
          :disabled="loading || purchasing || disabled"
          :options="paymentOptions"
        >
        </b-form-select>
        <div class="mt-1">
          <b-link
            v-b-modal:payment-method-form
            @click="() => $store.dispatch('paymentMethods/loadEmpty')"
          >
            {{ $t("components.user.add_credit_box.payment_method.add") | capitalize }}
          </b-link>
        </div>
      </b-col>
    </b-row>

    <b-modal
      id="payment-method-form"
      ref="modal"
      hide-header
      hide-footer
      @shown="paymentFormShown = true"
      @hidden="paymentFormShown = false"
    >
      <payment-method-form
        :shown="paymentFormShown"
        show-cancel
        @created="() => $bvModal.hide('payment-method-form')"
        @cancel="() => $bvModal.hide('payment-method-form')"
      />
    </b-modal>

    <b-row class="mt-3">
      <b-col class="text-center">
        <icon-button
          type="submit"
          variant="success"
          :disabled="
            !hasPaymentMethod || amount <= 0 || amount < normalizedMinimumRequired || disabled
          "
          :loading="loading || purchasing"
          @click="buyCredit"
        >
          {{ label }}
        </icon-button>
        <icon-button v-if="!noCancel" class="ml-2" role="cancel" @click="emitCancel">
          {{ $t("forms.cancel") | capitalize }}
        </icon-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import IconButton from "@/components/shared/IconButton.vue";
import { capitalize, currency, percent, normalizeCurrency } from "@/helpers/filters";
import { addFeeToAmount, feeSpec } from "@/helpers/stripeCreditCardHelper";
import AmountSelect from "@/components/Forms/AmountSelect.vue";

export default {
  name: "UserAddCreditBox",
  components: {
    AmountSelect,
    IconButton,
    PaymentMethodForm: () => import("@/components/PaymentMethod/PaymentMethodForm.vue"),
  },
  props: {
    // Minimum that needs to be paid.
    minimumRequired: {
      type: Number,
      required: false,
      default: 0,
    },
    actualCost: {
      type: Number,
      required: false,
      default: 0,
    },
    actualCostLabel: {
      type: String,
      default: function () {
        return this.$t("components.user.add_credit_box.actual_cost_label_default");
      },
    },
    paymentMethods: {
      type: Array,
      required: false,
      default: undefined,
    },
    // This prop is a patch to remove cancel button from the pre-payment step.
    noCancel: {
      type: Boolean,
      required: false,
      default: false,
    },
    addStandardOptions: {
      type: Boolean,
      required: false,
      default: false,
    },
    label: {
      type: String,
      default: function () {
        return this.$t("components.user.add_credit_box.buttons.add_funds");
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    noCustomAmount: {
      type: Boolean,
      default: false,
    },
    minimumDefault: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      amount: 0,
      purchasing: false,
      paymentMethodId: this.paymentMethods
        ? this.paymentMethods.find((p) => p.is_default)?.id
        : null,
      paymentFormShown: false,
    };
  },
  computed: {
    paymentMethod() {
      return this.paymentMethods?.find((p) => p.id === this.paymentMethodId);
    },
    fee() {
      return feeSpec(this.paymentMethod);
    },
    feeRatio() {
      return this.fee.ratio;
    },
    feeConstant() {
      return this.fee.constant;
    },
    amountWithFee() {
      return addFeeToAmount(this.amount, this.fee);
    },
    normalizedMinimumRequired() {
      return normalizeCurrency(this.minimumRequired);
    },
    paymentOptions() {
      if (!this.hasPaymentMethod) {
        return [];
      }
      return this.paymentMethods.map((pm) => {
        const { name: text, id: value, is_default: selected } = pm;
        return {
          text,
          value,
          selected,
        };
      });
    },
    hasPaymentMethod() {
      return this.paymentMethods && this.paymentMethods.length > 0;
    },
  },
  watch: {
    paymentMethods(newPaymentMethods) {
      if (!this.paymentMethodId) {
        this.paymentMethodId = newPaymentMethods
          ? newPaymentMethods.find((p) => p.is_default)?.id
          : null;
      }
    },
  },
  methods: {
    capitalize,
    currency,
    percent,
    emitCancel() {
      this.$emit("cancel");
    },
    async buyCredit() {
      this.purchasing = true;

      try {
        const { amount, paymentMethodId } = this;
        await this.$store.dispatch("account/buyCredit", {
          amount,
          paymentMethodId,
        });
        this.$emit("bought");
      } finally {
        this.purchasing = false;
      }
    },
  },
};
</script>

<style lang="scss">
.user-add-credit-box {
  .total {
    font-size: 3rem;
    color: $primary;
    font-weight: bold;
  }
}
</style>
